/* eslint-disable max-len */
import * as images from '../../components/assets/Images';
import * as icons from '../../components/assets/images/Icons';
import { AuthLayoutConfig } from '../../components/flows/layouts/AuthLayout';
import {
  getAuthMethodUsed,
  getHasDoctor,
  getPaymentRoute,
} from '../../util/ConditionalNextScreenSelectors';
import { FlowConfig, ScreenType, SharedScreenName } from '../FlowTypes';

const NEXT_BUTTON_TITLE = 'Next';

const privacyDeclarationStatement =
  'Finito will not sell any of your personal information. Ever. That’s a promise.';

export enum QuitScreenName {
  EducationHypnoQuit = 'educationHypnoQuit',
  WhyQuitNow = 'whyQuitNow',
  DailyCigaretteConsumption = 'dailyCigaretteConsumption',
  WhyDoYouSmoke = 'whyDoYouSmoke',
  ForRelaxing = 'forRelaxing',
  EducationRelaxesYouMyth = 'educationRelaxesYouMyth',
  Symptoms = 'symptoms',
  ImpactLife = 'impactLife',
  ImpactWhoElse = 'impactWhoElse',
  ImpactOnOthers = 'impactOnOthers',
  ImpactOnFuture = 'impactOnFuture',
  HowLikeToFeel = 'howLikeToFeel',
  HowLovedOnesFeel = 'howLovedOnesFeel',
  LookingAhead10Years = 'lookingAhead10Years',
  EducationHealing = 'educationHealing',
  IfYouKnew = 'ifYouKnew',
  QuitAttempts = 'quitAttempts',
  AreYouConfident = 'areYouConfident',
  EducationQuitAttempts = 'educationQuitAttempts',
  EducationTreatments = 'educationTreatments',
  SymptomIntensity = 'symptomIntensity',
  WithdrawalSymptoms = 'withdrawalSymptoms',
  EducationWithdrawalSymptoms = 'educationWithdrawalSymptoms',
  TimeOfDayHabit = 'timeOfDayHabit',
  ActivityHabit = 'activityHabit',
  ResultHabits = 'resultHabits',
  AreYouCommitted = 'areYouCommitted',
  ProgramReinforcement = 'programReinforcement',
  // Below screens were introduced in v4
  TimeOfDayFinito = 'timeOfDayFinito',
  SmokingAdvantages = 'smokingAdvantages',
  Suffering = 'suffering',
  HowReady = 'howReady',
  BetterLife = 'betterLife',
  PossibilitiesIn5years = 'possibilitiesIn5years',
  WhyBecomeFreeFromSmoking = 'whyBecomeFreeFromSmoking',
  FeelGoodAboutCommittingToStop = 'feelGoodAboutCommittingToStop',
  BrainNegativeSelfTalkRelateStatement = 'brainNegativeSelfTalkRelateStatement',
  EducationBrainNegativeSelfTalk = 'educationBrainNegativeSelfTalk',
  BrainStory = 'brainStory',
  BrainNicotineDependence = 'brainNicotineDependence',
  HardToBuildWillpowerRelateStatement = 'hardToBuildWillpowerRelateStatement',
  StructuralBrainChanges = 'structuralBrainChanges',
  StopSmokingWithSupport = 'stopSmokingWithSupport',
  HelpPrepareToStopSmoking = 'helpPrepareToStopSmoking',
  FinitoHypnosisProgram = 'finitoHypnosisProgram',
  TimeCommittment = 'timeCommittment',
  Committment = 'committment',
  SlowerResults = 'slowerResults',
  ResultsAfterOneWeek = 'resultsAfterOneWeek',
  LifeChanges = 'lifeChanges',
  SessionListenRecommendation = 'sessionListenRecommendation',
  SmokeTrigger = 'smokeTrigger',
  SmokeTriggerEducation = 'smokeTriggerEducation',
  StopDate = 'stopDate',
  PermanentNonSmoker = 'permanentNonSmoker',
  MoneyBackGuarantee = 'moneyBackGuarantee',
  AccountabilityAndMotivation = 'accountabilityAndMotivation',
  SupportAndCommunity = 'supportAndCommunity',
  ReadyToChange = 'readyToChange',
  NotReadyToChange = 'notReadyToChange',
  FeelAboutStopDate = 'feelAboutStopDate',
  FriendReferral = 'friendReferral',
  FriendReferralSent = 'friendReferralSent',
  NoFriendReferral = 'noFriendReferral',
}

export type QuitAssessmentScreen = SharedScreenName | QuitScreenName;

const authLayout: AuthLayoutConfig = {
  registerTitle: 'One last step,\n so we can finalize your results!',
  registerBody: ['Complete your profile to find out whether Finito could work for you.'],
  registerPrivacyDeclaration: [privacyDeclarationStatement],
  loginTitle: 'Welcome back!',
  registerButtonTitle: 'See my results',
  loginButtonTitle: 'See my program',
};

/**
 * This should include all web-onboarding screens (assessment, education, payment, etc.) for Quit.
 */
export const QUIT_CONFIG: FlowConfig<QuitAssessmentScreen> = {
  name: 'QuitConfig',
  showProgressBar: true,
  // We fire an `o-flow-entered` event manually from the `Onboard` page (see `trackFlowEnteredAnalytics`) instead of
  // here because it needs to include traits derived from experiments being run.
  startEvent: undefined,
  screens: [
    {
      screenName: SharedScreenName.Gender,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['What’s your gender?'],
        choices: ['Female', 'Male', 'Non-binary', 'Other', 'Prefer not to say'],
      },
      nextScreen: SharedScreenName.Age,
    },

    {
      screenName: SharedScreenName.Age,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['What’s your age?'],
        choices: ['20s', '30s', '40s', '50s', '60s', '70s+'],
      },
      nextScreen: QuitScreenName.EducationHypnoQuit,
    },

    {
      screenName: QuitScreenName.EducationHypnoQuit,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'People become dependent on smoking because it triggers the release of dopamine, a ‘feel good’ chemical, in the brain. This is why it can feel like your brain is convincing you to smoke.',
            textAlign: 'left',
          },
          {
            type: 'image',
            src: images.quitBrain,
            fullWidth: true,
          },
          {
            type: 'text',
            content: 'Finito uses hypnosis to help you build the skills to rewire your brain.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Let’s start by learning more about you, so we can assess whether Finito can help you stop smoking.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.HowLong,
    },

    {
      screenName: SharedScreenName.HowLong,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How long have you been wanting to stop smoking?'],
        choices: ['0-6 months', '6-12 months', '1-5 years', '5+ years'],
      },
      nextScreen: QuitScreenName.WhyQuitNow,
    },

    {
      screenName: QuitScreenName.WhyQuitNow,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Why do you want to become a non-smoker?'],
        choices: [
          'I’m ready to stop now',
          'I’m sick of feeling this way',
          'For my loved ones',
          'To save money',
          'I’m fed up with cravings',
        ],
      },
      nextScreen: SharedScreenName.Doctor,
    },

    {
      screenName: SharedScreenName.Doctor,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'A growing number of healthcare professionals are recommending Finito to stop smoking.',
          'Were you recommended Finito by a healthcare professional (e.g. a doctor or consultant)?',
        ],
        choices: ['Yes', 'No'],
      },
      nextScreen: {
        Yes: QuitScreenName.DailyCigaretteConsumption,
        No: SharedScreenName.Attribution,
        fallback: SharedScreenName.Attribution,
      },
    },

    {
      screenName: SharedScreenName.Attribution,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Where did you hear about us?'],
        choices: [
          'Facebook',
          'TikTok',
          'Reddit',
          'App Store',
          'Influencer',
          'Friends or family',
          'Instagram',
          'Search engine (Google, Bing)',
          'Blog post or news article',
          'Magazine or print',
          'Support group',
          'Other',
        ],
        randomizeChoices: true,
      },
      nextScreen: QuitScreenName.DailyCigaretteConsumption,
    },

    {
      screenName: QuitScreenName.DailyCigaretteConsumption,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How many cigarettes do you smoke each day?'],
        choices: ['Only a few', 'Half a pack', 'A pack a day', 'More than a pack a day'],
      },
      nextScreen: QuitScreenName.WhyDoYouSmoke,
    },

    {
      screenName: QuitScreenName.WhyDoYouSmoke,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Why did you start smoking?'],
        choices: [
          'To fit in',
          'My family were smokers',
          'To relieve stress',
          'I wanted to try it',
          'To be independent',
          'Other',
          'I don’t know why',
        ],
      },
      nextScreen: QuitScreenName.SmokingAdvantages,
    },

    {
      screenName: QuitScreenName.SmokingAdvantages,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What are the advantages of smoking (if any)?'],
        choices: [
          'To manage stress',
          'It’s my time out',
          'Something to do',
          'Gives me confidence',
          'It’s my coping mechanism',
          'Bonding with other people',
          'I like the taste or feel',
          'Other',
          'None',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ForRelaxing,
    },

    {
      screenName: QuitScreenName.ForRelaxing,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'How much do you relate to this statement?',
          '“Smoking is my way of relaxing. It makes me feel good.”',
        ],
        choices: ['Strongly agree', 'Agree', 'Disagree', 'Not sure'],
      },
      nextScreen: QuitScreenName.EducationRelaxesYouMyth,
    },

    {
      screenName: QuitScreenName.EducationRelaxesYouMyth,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content: 'Myth: smoking is relaxing.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Nicotine in cigarettes is actually a stimulant, making it the opposite of a relaxant.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'When you feel relaxed after smoking, it’s because you have temporarily relieved the withdrawal symptoms that nicotine itself is causing.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Breaking the habit for good will offer greater relaxation than the ‘quick fix’ provided by a cigarette.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.Symptoms,
    },

    {
      screenName: QuitScreenName.Symptoms,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What physical symptoms do you normally experience?'],
        choices: [
          'Breathing problems',
          'Saggy skin and wrinkles',
          'Tooth and gum disease',
          'Bad breath',
          'Hair losing its shine',
          'Yellow stains on fingers or teeth',
          'More regular cold and flu',
          'Chest pain',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ImpactLife,
    },

    {
      screenName: QuitScreenName.ImpactLife,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What suffering has smoking caused you in your life?'],
        choices: [
          'Confidence/self-esteem issues',
          'Financial problems',
          'Health problems',
          'Difficulty socializing',
          'Relationship issues',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ImpactWhoElse,
    },

    {
      screenName: QuitScreenName.ImpactWhoElse,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Who else in your life suffers because of your smoking?'],
        choices: ['Children', 'Partner', 'Family', 'Friends', 'Colleagues', 'Others', 'No one'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ImpactOnOthers,
    },

    {
      screenName: QuitScreenName.ImpactOnOthers,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['How are they impacted by your smoking habit?'],
        choices: [
          'Second-hand smoke',
          'Stress about my habit',
          'Concerns for my health',
          'Setting a bad example',
          'Other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ImpactOnFuture,
    },

    {
      screenName: QuitScreenName.ImpactOnFuture,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['If you don’t stop smoking, what will your future look like?'],
        choices: [
          'Health problems',
          'Financial problems',
          'Overburdened loved ones',
          'More anxiety and stress',
          'Children picking up my smoking habit',
          'Other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.HowReady,
    },

    {
      screenName: QuitScreenName.HowReady,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How ready are you to change your future for the better?'],
        choices: ['Very ready', 'Somewhat ready', 'Not at all ready'],
      },
      nextScreen: {
        'Very ready': QuitScreenName.ReadyToChange,
        'Somewhat ready': QuitScreenName.ReadyToChange,
        'Not at all ready': QuitScreenName.NotReadyToChange,
        fallback: QuitScreenName.ReadyToChange,
      },
    },

    {
      screenName: QuitScreenName.ReadyToChange,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'That’s great! The most important factor in successfully stopping smoking with hypnosis is that you want to change yourself.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Hypnosis is not a quick fix but it can be a powerful tool when you’ve made the decision to stop smoking.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.HowLikeToFeel,
    },

    {
      screenName: QuitScreenName.NotReadyToChange,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'That’s OK. Just remember that the most important factor in successfully stopping smoking with hypnosis is that you want to change yourself.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Hypnosis is not a quick fix but it can be a powerful tool when you’ve made the decision to stop smoking.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.HowLikeToFeel,
    },

    {
      screenName: QuitScreenName.HowLikeToFeel,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['OK, let’s look ahead and imagine you’ve stopped smoking.', 'How will you feel?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'In control of my life',
          'Healthier in my body',
          'Confident in myself',
          'No longer afraid of quitting',
          'Free from cigarette cravings',
          'Relaxed and calm',
          'Proud of myself',
          'More/other',
        ],
      },
      nextScreen: QuitScreenName.HowLovedOnesFeel,
    },

    {
      screenName: QuitScreenName.HowLovedOnesFeel,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['How will your loved ones (partner, children, friends, and family) feel?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: ['Relieved', 'Proud', 'Excited about the future', 'Motivated', 'Happy', 'Other'],
      },
      nextScreen: QuitScreenName.LifeChanges,
    },

    {
      screenName: QuitScreenName.LifeChanges,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['How will your life change?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'Improved health and fitness',
          'More money',
          'Better relationships',
          'Better tasting food',
          'Clean-smelling clothes and home',
          'More confidence and pride',
          'More/other',
        ],
      },
      nextScreen: QuitScreenName.PossibilitiesIn5years,
    },

    {
      screenName: QuitScreenName.PossibilitiesIn5years,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What does your life look like in five years’ time if you stop smoking?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'I’ll be spending more time with loved ones',
          'I’ll be traveling more',
          'I’ll have new hobbies',
          'I’ll have a better social life',
          'I’ll have more financial freedom',
          'More/other',
        ],
      },
      nextScreen: QuitScreenName.IfYouKnew,
    },

    {
      screenName: QuitScreenName.IfYouKnew,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'If you knew that it was possible to become a permanent non-smoker, would you start this journey today?',
        ],
        choices: ['Yes', 'No', 'Unsure'],
      },
      nextScreen: QuitScreenName.EducationHealing,
    },

    {
      screenName: QuitScreenName.EducationHealing,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'From the moment you stop smoking your body starts to heal itself. Within hours your blood pressure drops, your circulation improves and your risk of heart disease reduces.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitEducationHealing, fullWidth: true },
          {
            type: 'text',
            content:
              'A year after stopping, your risk of heart disease and smoking-related heart attack is cut in half.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'After five years, your risk of stroke can be the same as someone who’s never smoked at all.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.WhyBecomeFreeFromSmoking,
    },

    {
      screenName: QuitScreenName.WhyBecomeFreeFromSmoking,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['Why is becoming free from smoking important to you?'],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
        choices: [
          'To be a good role model',
          'To take back control of my life',
          'To prove to myself that I can',
          'To make the most of my life',
          'To enjoy a healthier body',
          'To improve my confidence and self-esteem',
          'More/other',
        ],
      },
      nextScreen: QuitScreenName.FeelGoodAboutCommittingToStop,
    },

    {
      screenName: QuitScreenName.FeelGoodAboutCommittingToStop,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'How much do you agree with this statement?',
          '“I would feel good about myself if I committed to stop smoking today.”',
        ],
        choices: ['Strongly agree', 'Agree', 'Disagree', 'Not sure'],
      },
      nextScreen: SharedScreenName.Auth,
    },

    {
      screenName: SharedScreenName.Auth,
      type: ScreenType.Auth,
      layout: { ...authLayout },
      nextScreen: {
        loginAndAlreadyPaid: SharedScreenName.AlreadyPaidDownloadApp,
        login: SharedScreenName.Results,
        register: SharedScreenName.LoadingResults,
        fallback: SharedScreenName.Results,
      },
      nextScreenSelector: getAuthMethodUsed,
    },

    {
      screenName: SharedScreenName.AlreadyPaidDownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
        // Second line of Title coming from translation JSON to avoid 'missing translation' bug.
        title: ['You already have access!'],
        body: ['Use the same email and password you signed up with:'],
        alreadyPaid: true,
      },
    },

    {
      screenName: SharedScreenName.LoadingResults,
      type: ScreenType.LoadingResults,
      layout: {
        reviewer: {
          name: 'Sally',
          image: images.linda,
          testimonial: [
            '“I’ve been a heavy smoker for 40 years, with dozens of attempt at stopping, all temporary. Finito is different. The hypnosis worked fantastically for me and it’s now 5 weeks without a puff and counting.”',
          ],
          date: 'November 28, 2022',
        },
      },
      nextScreen: SharedScreenName.Results,
    },

    {
      screenName: SharedScreenName.Results,
      type: ScreenType.Results,
      layout: {
        title: 'The results are in',
        summaryBoxHeading: 'Finito is likely to work for you!',
        summaryBoxBody:
          'Similar people to you were 10x more likely to stop smoking with hypnosis than going cold turkey.',
        body: [
          'Your survey results suggest that you are a great candidate for hypnosis. Congratulations!',
          'While Finito isn’t magic, it can be a life-changing tool. We’ve already helped thousands of people on their journey to stop smoking.',
          'Let’s find out how Finito can help you become a non-smoker, and we’ll start building your program!',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.QuitAttempts,
    },

    {
      screenName: QuitScreenName.QuitAttempts,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How many times have you tried to stop smoking before?'],
        choices: ['1-5 times', '5-10 times', '10-20 times', '20-30 times', '30+ times', 'Never'],
      },
      nextScreen: QuitScreenName.AreYouConfident,
    },
    {
      screenName: QuitScreenName.AreYouConfident,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How confident are you about stopping smoking on your own?'],
        choices: ['Confident', 'A little', 'No chance'],
      },
      nextScreen: QuitScreenName.EducationQuitAttempts,
    },

    {
      screenName: QuitScreenName.EducationQuitAttempts,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content: 'It’s hard to stop smoking alone.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'According to researchers at the University of Toronto, on average, it will take a smoker 30 or more attempts before successfully abstaining for 1 year or longer.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitEducationQuitAttempts, fullWidth: true },
          {
            type: 'text',
            content: 'Often this is because they are quitting ‘cold turkey’ without any support.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'The good news is that you are 10x more likely to stop smoking with hypnotherapy.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Finito will give you the support you need with an evidence-based program that combines hypnosis with therapy (hypnotherapy) to rewire your brain and change your habits.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.BrainNegativeSelfTalkRelateStatement,
    },

    {
      screenName: QuitScreenName.BrainNegativeSelfTalkRelateStatement,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'How much do you relate to this statement?',
          '“The chatterbox in my brain has a lot of negative self-talk.”',
        ],
        choices: ['Strongly agree', 'Agree', 'Disagree', 'Not sure'],
      },
      nextScreen: QuitScreenName.EducationBrainNegativeSelfTalk,
    },

    {
      screenName: QuitScreenName.EducationBrainNegativeSelfTalk,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'Sometimes our internal chatterbox can tell us that we’re worthless or not good enough.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content: 'Because of this, we can self-sabotage our attempts to change.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content: 'Finito can help change the conversation in your brain.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitEducationTreatments, fullWidth: true },
          {
            type: 'text',
            content:
              'It uses hypnosis to relax you into a deeply-focused state where the noisy, conscious part of your brain quiets down so that you can receive new ideas.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.BrainStory,
    },

    {
      screenName: QuitScreenName.BrainStory,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What story does your brain tell you that stops you from becoming a non-smoker?'],
        choices: [
          'I’m afraid I’ll fail',
          'I can’t change my habits',
          'I’m waiting for the right time',
          'I’m not confident',
          'Smoking makes me feel good',
          'I haven’t got the willpower',
          'Other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.BrainNicotineDependence,
    },

    {
      screenName: QuitScreenName.BrainNicotineDependence,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'Your brain wants you to keep smoking. It has developed a dependence on nicotine, so it makes up excuses to prevent you from stopping.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'This is because smoking triggers the release of dopamine, a ‘feel good’ chemical. The brain uses smoking as a self-soothing tool to regulate your emotions.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitBrain2 },
          {
            type: 'text',
            content:
              'This is why you need to find another tool to make you feel better and regulate your emotions. Otherwise, you’ll quickly return to smoking.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content: 'Finito can help you learn new self-soothing tools.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.HardToBuildWillpowerRelateStatement,
    },

    {
      screenName: QuitScreenName.HardToBuildWillpowerRelateStatement,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'How much do you relate to this statement?',
          '“I find it hard to build enough willpower to stop smoking.”',
        ],
        choices: ['Strongly agree', 'Agree', 'Disagree', 'Not sure'],
      },
      nextScreen: QuitScreenName.StructuralBrainChanges,
    },

    {
      screenName: QuitScreenName.StructuralBrainChanges,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'Chronic consumption of any substance, including nicotine, leads to structural brain changes.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Over time your brain takes less pleasure from these substances, which leads to less willpower and more cravings. This is why long-term habits such as smoking can be so hard to break.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Finito’s hypnotherapy can help you build craving management skills and stronger willpower so that you are more prepared to stop smoking.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.SymptomIntensity,
    },

    {
      screenName: QuitScreenName.SymptomIntensity,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Have you had withdrawal symptoms when you tried to stop smoking in the past?'],
        choices: ['Severe symptoms', 'Moderate symptoms', 'Mild symptoms', 'No symptoms'],
      },
      nextScreen: QuitScreenName.WithdrawalSymptoms,
    },

    {
      screenName: QuitScreenName.WithdrawalSymptoms,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What withdrawal symptoms have you experienced?'],
        choices: [
          'Depression',
          'Irritability',
          'Anger',
          'Hunger',
          'Insomnia',
          'Nicotine cravings',
          'Other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.EducationWithdrawalSymptoms,
    },

    {
      screenName: QuitScreenName.EducationWithdrawalSymptoms,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'Withdrawal symptoms can be a frustrating part of stopping smoking. However, they are a positive sign that nicotine is leaving your body.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitEducationWithdrawalSymptoms, fullWidth: true },
          {
            type: 'text',
            content:
              'The most severe symptoms are often felt when you stop cold turkey without any support.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'But you can take the time to prepare your body and mind so that the intensity is lessened.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'With Finito, you do this by gradually reducing your cigarettes so that there is less nicotine in your body and withdrawal symptoms feel less severe.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Finito also helps you prepare your brain with hypnotherapy so that you feel more confident and relaxed during the process.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.StopSmokingWithSupport,
    },

    {
      screenName: QuitScreenName.StopSmokingWithSupport,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['Do you believe that you can stop smoking with the right support?'],
        choices: ['Yes', 'Maybe', 'Not sure'],
      },
      nextScreen: QuitScreenName.HelpPrepareToStopSmoking,
    },

    {
      screenName: QuitScreenName.HelpPrepareToStopSmoking,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What would help you feel more prepared to stop smoking?'],
        choices: [
          'Less negative self-talk',
          'Increased willpower',
          'Manage cravings and withdrawal symptoms',
          'A new way to regulate my mood',
          'Advice and education',
          'Support from others',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.FinitoHypnosisProgram,
    },

    {
      screenName: QuitScreenName.FinitoHypnosisProgram,
      type: ScreenType.Presentation,
      layout: {
        content: [
          { type: 'image', src: images.quitSessionAndSignupScreenshots },
          {
            type: 'text',
            content:
              'Finito will help you feel prepared to stop smoking by rewiring your brain while you reduce the amount you smoke.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'You’ll be given a personalized hypnotherapy program which includes a Stop Date, which is when you will stop smoking.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'In preparation for this date, you’ll listen to a hypnosis session every day, and you’ll gradually reduce the number of cigarettes you smoke so that you feel confident and ready.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.TimeCommittment,
    },

    {
      screenName: QuitScreenName.TimeCommittment,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How much time can you commit each day to stop smoking?'],
        choices: ['15 minutes', '15-30 minutes', '30-60 minutes', 'More than 1 hour'],
      },
      nextScreen: QuitScreenName.ResultsAfterOneWeek,
    },

    {
      screenName: QuitScreenName.ResultsAfterOneWeek,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What results would you like to see after one week of Finito?'],
        choices: [
          'Reduction in my cigarettes',
          'Feeling prepared to stop smoking',
          'Feeling more relaxed',
          'A better understanding of myself',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.TrialExplanation,
    },

    {
      screenName: SharedScreenName.TrialExplanation,
      type: ScreenType.TrialExplanation,
      layout: {
        body: [
          'Within the first week of Finito, our goal is to help you smoke less and be prepared to stop smoking for good.',
          'This is why we offer a 7-day trial to show you what’s possible.',
        ],
        image: images.quitProgram7DayTrial,
        imageAltText: 'Finito payment timeline',
        primaryButtonTitle: 'Okay',
      },
      nextScreen: QuitScreenName.SmokeTrigger,
    },

    {
      screenName: QuitScreenName.SmokeTrigger,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['When do you usually smoke?'],
        choices: [
          'At certain times of the day',
          'When I’m doing certain things',
          'When I feel a certain way',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.SmokeTriggerEducation,
    },

    {
      screenName: QuitScreenName.SmokeTriggerEducation,
      type: ScreenType.Presentation,
      layout: {
        content: [
          { type: 'image', src: images.quitTodayScreen },
          {
            type: 'text',
            content:
              'Triggers to smoke can be simple activities such as finishing a meal, being in a certain place, driving, pouring a drink, or being in a social setting. Or the urge to smoke can be driven by daily rituals or strong emotions.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Your Finito program includes daily hypnotherapy, structured educational reading and daily tasks to identify and manage your triggers.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.TimeOfDayFinito,
    },

    {
      screenName: QuitScreenName.TimeOfDayFinito,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['When do you want to listen to Finito?'],
        choices: ['When I wake up', 'Morning', 'Lunchtime', 'Afternoon/evening', 'Before bed'],
      },
      nextScreen: QuitScreenName.SessionListenRecommendation,
    },
    {
      screenName: QuitScreenName.SessionListenRecommendation,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'Finito’s hypnotherapy sessions are just 15 minutes long, so you can listen at any time of the day.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Repetition is key, which is why we recommend listening every day, or even multiple times a day.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Like any exercise, the more you listen, the more you are training your brain’s ‘stop smoking’ muscle, making it stronger.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.AccountabilityAndMotivation,
    },

    {
      screenName: QuitScreenName.AccountabilityAndMotivation,
      type: ScreenType.MultiSelect,
      layout: {
        body: ['What would help you stay accountable while using Finito?'],
        choices: [
          'Access to coaches',
          'Regular check-ins',
          'Encouraging emails',
          'Community support',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.SupportAndCommunity,
    },

    {
      screenName: QuitScreenName.SupportAndCommunity,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content: 'Research shows that feeling supported increases your success rate.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'As part of the program, you’ll be able to join our private Facebook group so that you are surrounded by like-minded people on the same journey.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'If you don’t use Facebook, that’s OK. You’ll still have access to our dedicated support team of coaches as well as regular check-ins via email.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.FriendReferral,
    },

    {
      screenName: QuitScreenName.FriendReferral,
      type: ScreenType.Referral,
      layout: {
        title: 'Get even more support with a quit buddy',
        content: [
          {
            type: 'text',
            content:
              'Studies have shown that you’re up to 3 times more likely to quit when you pair up with a quit buddy\u00B9.',
            textAlign: 'left',
          },
          {
            type: 'image',
            src: images.peopleRidingBicycle,
            fullWidth: true,
            altText: 'People riding a bicycle',
          },
        ],
        footnotes: [
          'Kviz, Frederick J., et al. "Use and effectiveness of buddy support in a self-help smoking cessation program." American Journal of Health Promotion 8, no. 3 (1994): 191-201.',
        ],
        primaryButtonTitle: 'Send',
        secondaryButtonTitle: 'Maybe another time',
      },
      nextScreen: {
        Yes: QuitScreenName.FriendReferralSent, // Send
        No: QuitScreenName.NoFriendReferral, // Maybe another time
        fallback: SharedScreenName.HypnotisabilityInfo,
      },
    },

    {
      screenName: QuitScreenName.FriendReferralSent,
      type: ScreenType.ShareLink,
      layout: {
        title: 'Get ready to team up...',
        content: [
          {
            type: 'text',
            content: 'Done – invite sent!',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'Are there more people wanting to quit in your circle? You can invite them using this handy link below.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.HypnotisabilityInfo,
    },

    {
      screenName: QuitScreenName.NoFriendReferral,
      type: ScreenType.ShareLink,
      layout: {
        title: 'Don’t know the email?',
        content: [
          {
            type: 'text',
            content: 'That’s okay!',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'If you do feel ready to team up with someone in mind – you can invite them using the handy link below.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.HypnotisabilityInfo,
    },

    {
      screenName: SharedScreenName.HypnotisabilityInfo,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'To help us finalize your Stop Date, let’s understand your current skills with hypnotherapy and how hypnotizable you currently are.',
          },
          {
            type: 'text',
            content:
              'Hypnotizability refers to how well you can intensely focus your attention and be open and responsive to positive suggestions (like learning to manage cravings).',
          },
          {
            type: 'text',
            content:
              'The next 5 questions will help us to determine how hypnotizable you are, so we can maximize your results with the Finito program.',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityVividRecall,
    },

    {
      screenName: SharedScreenName.HypnotisabilityVividRecall,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'If I want to, I have the ability to recall an event so vividly that I can feel as though I am reliving it.',
        ],
        choices: [
          {
            value: '1',
            label: 'I am unable to do this',
          },
          {
            value: '2',
            label: 'I am barely able to do this',
          },
          {
            value: '3',
            label: 'I am partially able to do this',
          },
          {
            value: '4',
            label: 'I am able to do this',
          },
          {
            value: '5',
            label: 'I am very capable of doing this',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityFocus,
    },

    {
      screenName: SharedScreenName.HypnotisabilityFocus,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'If I choose to, I can focus so completely on reading a book or on my thoughts that I lose track of time and what is happening around me.',
        ],
        choices: [
          {
            value: '1',
            label: 'I am unable to do this',
          },
          {
            value: '2',
            label: 'I am barely able to do this',
          },
          {
            value: '3',
            label: 'I am partially able to do this',
          },
          {
            value: '4',
            label: 'I am able to do this',
          },
          {
            value: '5',
            label: 'I am very capable of doing this',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityAbsorption,
    },

    {
      screenName: SharedScreenName.HypnotisabilityAbsorption,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'If I choose to, I have the ability to be so absorbed in a television show or movie that I can feel what the characters might be feeling.',
        ],
        choices: [
          {
            value: '1',
            label: 'I am unable to do this',
          },
          {
            value: '2',
            label: 'I am barely able to do this',
          },
          {
            value: '3',
            label: 'I am partially able to do this',
          },
          {
            value: '4',
            label: 'I am able to do this',
          },
          {
            value: '5',
            label: 'I am very capable of doing this',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityIntuition,
    },

    {
      screenName: SharedScreenName.HypnotisabilityIntuition,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'I have the ability to be very aware of and trust my own intuitions (or feelings) about things as they happen or may happen in the future.',
        ],
        choices: [
          {
            value: '1',
            label: 'I am unable to do this',
          },
          {
            value: '2',
            label: 'I am barely able to do this',
          },
          {
            value: '3',
            label: 'I am partially able to do this',
          },
          {
            value: '4',
            label: 'I am able to do this',
          },
          {
            value: '5',
            label: 'I am very capable of doing this',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityColorVisualization,
    },

    {
      screenName: SharedScreenName.HypnotisabilityColorVisualization,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'If I choose to, I can imagine seeing the color of grass change from green to another color (such as blue or red) and actually see the color change.',
        ],
        choices: [
          {
            value: '1',
            label: 'I am unable to do this',
          },
          {
            value: '2',
            label: 'I am barely able to do this',
          },
          {
            value: '3',
            label: 'I am partially able to do this',
          },
          {
            value: '4',
            label: 'I am able to do this',
          },
          {
            value: '5',
            label: 'I am very capable of doing this',
          },
        ],
      },
      nextScreen: SharedScreenName.HypnotisabilityResults,
    },
    {
      screenName: SharedScreenName.HypnotisabilityResults,
      type: ScreenType.HypnotisabilityResults,
      showHeaderBar: true,
      nextScreen: SharedScreenName.CoreProgramExperience,
    },
    {
      screenName: SharedScreenName.CoreProgramExperience,
      type: ScreenType.ProgramExperience,
      layout: {
        title: 'Your Stop Date',
        topBody: [
          'Based on the information provided, if you start your program today, your personalized Stop Date will be...',
        ],
        showDate: true,
        bottomBody: [
          'For the next 8 days, you’ll be provided with the tools to train your brain to stop smoking.',
        ],
        listTitle: 'In preparation for your Stop Date, you’ll…',
        listItems: [
          { image: icons.volumeTurquoise, desc: 'Listen to daily hypnosis' },
          { image: icons.booksTurquoise, desc: 'Access the Craving Kit' },
          { image: icons.hcpTurquoise, desc: 'Gain expert stop smoking tips' },
          { image: icons.stressTurquoise, desc: 'Learn to manage stress' },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.MaintenanceExperience,
    },

    {
      screenName: SharedScreenName.MaintenanceExperience,
      type: ScreenType.ProgramExperience,
      layout: {
        topBody: [
          'Finito will help you stop smoking, and it will also help you create a new identity.',
          'You’ll be able to strengthen your mind’s willpower, manage stress, and create new habits.',
          'You’ll also be able to use the Craving Kit for instant relief with short hypnotherapy sessions and breathing exercises.',
        ],
        listTitle: 'After your Stop Date, you’ll…',
        listItems: [
          { image: icons.crossTurquoise, desc: 'Stay a non-smoker' },
          { image: icons.wheelTurquoise, desc: 'Access your Craving Kit' },
          { image: icons.airTurquoise, desc: 'Continue breathing exercises' },
          { image: icons.heartTurquoise, desc: 'Access ongoing support' },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.FeelAboutStopDate,
    },

    {
      screenName: QuitScreenName.FeelAboutStopDate,
      type: ScreenType.SingleSelect,
      layout: {
        body: ['How do you feel about your Stop Date?'],
        choices: ['Hopeful', 'Excited', 'Ready', 'Nervous', 'Not sure', 'Other'],
      },
      nextScreen: QuitScreenName.MoneyBackGuarantee,
    },

    {
      screenName: QuitScreenName.MoneyBackGuarantee,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content: 'Believing in yourself is half the journey.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'If you’re not there yet, we’ll get you there! With daily hypnotherapy, readings, success stories and activities, you’ll feel more confident and ready to stop smoking in time for your Stop Date. Just make sure to trust the process, and it will happen for you.',
            textAlign: 'left',
          },
          { type: 'image', src: images.moneyBackGuarantee },
          {
            type: 'text',
            content:
              'We’re so confident that you can become a non-smoker that we’re offering you a money-back guarantee. At any time in the future, you can claim a refund. No questions asked.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.PermanentNonSmoker,
    },

    {
      screenName: QuitScreenName.PermanentNonSmoker,
      type: ScreenType.MultiSelect,
      layout: {
        body: [
          'Finito can help you stop smoking in a matter of weeks, but it can also help you change your lifestyle to remain a non-smoker forever.',
          'Which of these areas would you like to be supported with after you stop?',
        ],
        choices: [
          'New self-beliefs',
          'Turning down cigarettes in social settings',
          'Managing my weight',
          'Not smoking with alcohol',
          'Finding new ways to manage stress',
          'Create new positive habits',
          'More/other',
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: QuitScreenName.ProgramReinforcement,
    },

    {
      screenName: QuitScreenName.ProgramReinforcement,
      type: ScreenType.Presentation,
      layout: {
        content: [
          { type: 'heading', content: 'Long-term stop-smoking support' },
          {
            type: 'text',
            content: 'Finito will help you create a new identity as a permanent non-smoker.',
            textAlign: 'left',
          },
          { type: 'image', src: images.quitReinforcementScreen },
          {
            type: 'text',
            content:
              'At the end of your program, you’ll be moved onto a reinforcement mode. You’ll be able to listen to hypnosis for as long as you need to help solidify your new non-smoker identity and manage difficult situations.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'At this stage of your journey, you will unlock your customizable stop-smoking support tools and have access to new hypnotherapy sessions as they’re released.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: NEXT_BUTTON_TITLE,
      },
      nextScreen: SharedScreenName.ProgramStructure,
    },

    {
      screenName: SharedScreenName.ProgramStructure,
      type: ScreenType.ProgramStructure,
      layout: {
        title: 'Ready? Here’s your personalized\nFinito program',
        coreProgramWeekTitles: [
          'Prepare and Reduce',
          'Become Free from Smoking',
          'Build a Non-Smoker Life',
        ],
        postProgramTitle: 'Post 3-week program',
        postProgramContent: 'Reinforcement plan',
        primaryButtonTitle: 'Okay!',
      },
      nextScreen: QuitScreenName.Committment,
    },

    {
      screenName: QuitScreenName.Committment,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'As part of the trial, you’ll have access to the first week of your personalized program.',
          'Will you commit to listening to 5 sessions this week to give yourself the best chance of success?',
        ],
        choices: ['Yes', 'No', 'I’m not sure'],
      },
      nextScreen: {
        Yes: SharedScreenName.PhoneNumber,
        No: QuitScreenName.SlowerResults,
        'I’m not sure': QuitScreenName.SlowerResults,
        fallback: QuitScreenName.SlowerResults,
      },
    },

    {
      screenName: QuitScreenName.SlowerResults,
      type: ScreenType.Presentation,
      layout: {
        content: [
          {
            type: 'text',
            content:
              'People who listen to at least five sessions per week are 6x more likely to complete the program.',
            textAlign: 'left',
          },
          {
            type: 'text',
            content:
              'If you feel like you won’t be able to commit to this, that’s OK. Finito can still work for you, but it may take longer to see results.',
            textAlign: 'left',
          },
        ],
        primaryButtonTitle: 'Okay',
      },
      nextScreen: SharedScreenName.PhoneNumber,
    },
    {
      screenName: SharedScreenName.PhoneNumber,
      type: ScreenType.PhoneNumber,
      layout: {
        title: 'Daily Motivation',
        topBody: [
          'Sometimes the simplest things can keep us on track. Research shows that a daily motivational SMS can increase your chances of success.',
          'As part of the program, you’ll receive an inspirational and supportive SMS every day.',
          'Enter your phone number here:',
        ],
        bottomBody: [privacyDeclarationStatement],
        primaryButtonTitle: 'Continue',
      },
      nextScreen: {
        hasDoctor: SharedScreenName.Payment,
        fallback: SharedScreenName.WorksBest,
      },
      nextScreenSelector: getHasDoctor,
    },

    {
      screenName: SharedScreenName.WorksBest,
      type: ScreenType.WorksBest,
      layout: {
        title: 'What works best for you?',
        body: [
          'We know that trying something new can be scary.',
          'Your health is our priority and money shouldn’t stop you from exploring something that could change your life!',
        ],
        description: [
          'It costs us {{trialPrice}} to provide a {{trialPeriod}}-day trial.',
          'Please choose the amount below that best supports your circumstance.',
        ],
        primaryButtonTitle: 'Continue',
      },
      nextScreen: SharedScreenName.Payment,
    },

    {
      screenName: SharedScreenName.Payment,
      type: ScreenType.Payment,
      showHeaderBar: false,
      nextScreen: {
        newUser: SharedScreenName.FinishAccount,
        existingUser: SharedScreenName.DownloadApp,
        fallback: SharedScreenName.DownloadApp,
      },
      nextScreenSelector: getPaymentRoute,
    },

    {
      screenName: SharedScreenName.FinishAccount,
      type: ScreenType.FinishAccount,
      layout: {
        subtitle: 'Fill in these last few details and you’re ready to go!',
        primaryButtonTitle: 'Continue',
      },
      nextScreen: SharedScreenName.DownloadApp,
    },

    {
      screenName: SharedScreenName.DownloadApp,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You’re all set!', 'Access your program now'],
        // Cannot call `translate()` from config without calling `Localization.initialize()` in the same file.
        // body coming from translation JSON to avoid 'missing translation' bug.
        alreadyPaid: false,
      },
    },
  ],
};
